/**
 * Client liest sein eigenes Firmenprofil (Read-only).
 * Edit nur in draft/rejected — danach läuft alles über Internal Staff.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { DataCard } from '../components/DataCard.js';
import { DocumentUploader } from '../components/DocumentUploader.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useAuth } from '../hooks/useAuth.js';
import { requireRole } from '../lib/auth-guard.js';
import { formatAddress, formatBoolean, formatPeople } from '../lib/profile-formatters.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/client/profile')({
  component: ClientProfile,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['client']);
  },
});

function ClientProfile() {
  const { logout } = useAuth();
  const profile = trpc.clients.getMine.useQuery();

  if (profile.isLoading) {
    return <p className="p-6 text-[var(--muted-foreground)]">Lade Profil …</p>;
  }
  if (profile.error || !profile.data) {
    return (
      <p className="p-6 text-[oklch(0.55_0.2_25)]">
        Fehler: {profile.error?.message ?? 'Profil nicht gefunden'}
      </p>
    );
  }

  const c = profile.data;
  const editable = c.status === 'draft' || c.status === 'rejected';

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/client"
            className="text-sm text-[var(--muted-foreground)] hover:text-foreground"
          >
            ← Kunden-Bereich
          </Link>
          <h1 className="text-3xl font-bold mt-2">{c.companyName || 'Mein Firmenprofil'}</h1>
          <p className="text-[var(--muted-foreground)] mt-1">
            Status: <StatusBadge status={c.status} />
            {' · '}
            zuletzt geändert {new Date(c.updatedAt).toLocaleString('de-DE')}
          </p>
        </div>
        <div className="flex gap-2">
          {editable && (
            <Link to="/client/onboarding">
              <Button>Profil bearbeiten</Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => logout()}>
            Abmelden
          </Button>
        </div>
      </header>

      {c.status === 'rejected' && (
        <Card className="border-[oklch(0.55_0.2_25)]/40">
          <CardHeader>
            <CardTitle>Profil abgelehnt</CardTitle>
            <CardDescription>
              Internal Staff hat dein Firmenprofil zurückgewiesen. Bitte korrigieren und erneut
              einreichen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {c.reviewNotes || 'Keine Detailnotiz hinterlegt — bitte Internal Staff kontaktieren.'}
            </p>
          </CardContent>
        </Card>
      )}

      {c.status === 'pending_review' && (
        <Card>
          <CardContent className="pt-6 text-sm text-[var(--muted-foreground)]">
            Firmenprofil ist eingereicht und wird geprüft.
          </CardContent>
        </Card>
      )}

      {c.status === 'approved' && (
        <Card>
          <CardContent className="pt-6 text-sm text-[var(--muted-foreground)]">
            Firmenprofil freigegeben. Änderungen sind nur über Internal Staff möglich.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <DataCard
          title="Firma"
          rows={[
            ['Firmenname', c.companyName],
            ['Rechtsform', c.legalForm],
            [
              'Sitz',
              formatAddress(
                c.registeredAddressStreet,
                c.registeredAddressZip,
                c.registeredAddressCity,
              ),
            ],
            ['HRB-Nummer', c.hrbNumber],
            ['Amtsgericht', c.districtCourt],
            ['USt-ID', c.vatId],
            ['Geschäftsführer', formatPeople(c.managingDirectors)],
            ['Zeichnungsberechtigte', formatPeople(c.authorizedSignatories)],
          ]}
        />
        <DataCard
          title="Rechnungswesen"
          rows={[
            ['Rechnungs-E-Mail', c.billingEmail],
            ['Abw. Rechnungsadresse', formatBoolean(c.billingAddressDifferent)],
            ...(c.billingAddressDifferent
              ? ([
                  [
                    'Rechnungsadresse',
                    formatAddress(
                      c.billingAddressStreet,
                      c.billingAddressZip,
                      c.billingAddressCity,
                    ),
                  ],
                ] as [string, string | null][])
              : []),
            ['Zahlungsziel (Tage)', c.paymentTermsDays?.toString()],
            ['Skonto', c.earlyPaymentDiscount],
            ['PO-Nr. erforderlich', formatBoolean(c.requirePoNumber)],
            ['Kostenstelle erforderlich', formatBoolean(c.requireCostCenter)],
          ]}
        />
        <DataCard
          title="Operativ"
          rows={[
            ['Zeiterfassungs-Verfahren', c.timeTrackingMethod],
            ['Vor-Ort-Ansprechpartner', formatPeople(c.onsiteContacts)],
          ]}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dokumente</CardTitle>
          <CardDescription>
            {editable
              ? 'Du kannst weitere Dokumente hochladen oder ersetzen.'
              : 'Read-only — Änderungen sind nur über Internal Staff möglich.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentUploader ownerType="client" ownerId={c.id} readOnly={!editable} />
        </CardContent>
      </Card>
    </div>
  );
}
